/**
 * The unmatched-reasons drawer: its path suffix and its reason vocabulary, in ONE place.
 *
 * The API registers `<report path> + UNMATCHED_REASONS_PATH` and the dashboard requests
 * the same concatenation. Written out separately in the two apps they would be a literal
 * string that has to agree across a package boundary with nothing checking it — the
 * failure being a 404 that only appears when a merchant opens the drawer, which no test
 * exercising either side alone would catch.
 *
 * `check-report-sort-keys.mjs` cannot cover it: detail routes have no dashboard catalog
 * entry to compare against. Sharing the constant removes the need for a check at all.
 */
export const UNMATCHED_REASONS_PATH = "/unmatched-reasons";

/**
 * Why an order could not be placed against a campaign.
 *
 * The order here is the order the drawer lists them in when counts tie, and it runs from
 * "nothing anyone can do" to "someone can fix this today" on purpose — a merchant reading
 * top to bottom meets the unfixable first and stops blaming their agency for it.
 */
export const UNMATCHED_REASONS = [
  "no_visit_data",
  "click_id_only",
  "utm_without_campaign_id",
  "agency_code",
  "campaign_id_malformed",
  "campaign_id_unmatched",
  "macro_not_expanded",
] as const;

export type UnmatchedReason = (typeof UNMATCHED_REASONS)[number];

export interface UnmatchedReasonRow {
  readonly reason: UnmatchedReason;
  /** UInt64 from ClickHouse, which JSONEachRow renders as a string. */
  readonly orders: string;
  readonly revenue: number;
}

/** Who can act on each reason — the drawer groups by this. */
export const UNMATCHED_REASON_OWNER: Record<UnmatchedReason, "nobody" | "agency" | "you"> = {
  no_visit_data: "nobody",
  click_id_only: "agency",
  utm_without_campaign_id: "agency",
  macro_not_expanded: "agency",
  agency_code: "agency",
  campaign_id_malformed: "agency",
  campaign_id_unmatched: "you",
};
